import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppStateInterface } from 'app/core/app-config/state/models/app-state.interface';
import { TaskInterface, TaskTemplateInterface } from 'app/task/models';
import { CloseTask, CreateSurveyTask, CreateTask, LoadTasks, LoadTaskTemplates, UpdateTask } from 'app/task/state/actions';
import { Observable } from 'rxjs/internal/Observable';
import { map } from 'rxjs/operators';


import { TaskServiceInterface } from './task-service.interface';

export class TaskServiceFilters {

  public assignedToId?: number;
  public completedById?: number;
  public isComplete?: boolean;
  public subjectId?: number;

  constructor({
    assignedToId = undefined,
    completedById = undefined,
    isComplete = undefined,
    subjectId = undefined,
  }: TaskServiceFilters) {
    this.assignedToId = assignedToId;
    this.completedById = completedById;
    this.isComplete = isComplete;
    this.subjectId = subjectId;
  }
}

@Injectable()
export class TaskService implements TaskServiceInterface {


  constructor(private readonly _store: Store<AppStateInterface>) {
    this.onInit();
  }

  public onInit(): void {}

  public findAllOpenTasksAssignedToUserId(userId: number, reloadTasks: boolean = false): Observable<TaskInterface[]> {

    return this._store.select(({ task }: AppStateInterface) => task.tasks)
      .pipe(
        map((taskEntities: { [id: number ]: TaskInterface }) => {

          return Object.values(taskEntities).filter((aTask: TaskInterface) => {
            return !aTask.isComplete && aTask.assignedTo && aTask.assignedTo.id === userId;
          });
        }),
      );
  }

  public findAllOpenTasksForUserId(userId: number, reloadTasks: boolean = false): Observable<TaskInterface[]> {

    return this._store.select(({ task }: AppStateInterface) => task.tasks)
      .pipe(
        map((taskEntities: { [id: number ]: TaskInterface }) => {

          return Object.values(taskEntities).filter((aTask: TaskInterface) => {
            return !aTask.isComplete && aTask.subject.id === userId;
          });
        }),
      );
  }

  public findAllOpenTasksWhereTheSubjectIsUserId(userId: number,
                                                 reloadTasks: boolean = false): Observable<TaskInterface[]> {

    return this._store.select(({ task }: AppStateInterface) => task.tasks)
      .pipe(
        map((taskEntities: { [id: number ]: TaskInterface }) => {

          return Object.values(taskEntities)
            .filter((aTask: TaskInterface) => {
              const subjectIsUserId: boolean = aTask.subject && aTask.subject.id === userId;
              const isNotAssignedToUserId: boolean = aTask.assignedTo && aTask.assignedTo.id !== userId;

              return !aTask.isComplete && subjectIsUserId && isNotAssignedToUserId;
            });
        }),
      );
  }

  public findAllTasksCompletedByAndForUserId(userId: number,
                                             reloadTasks: boolean = false): Observable<TaskInterface[]> {

    return this._store.select(({ task }: AppStateInterface) => task.tasks)
      .pipe(
        map((taskEntities: { [id: number ]: TaskInterface }) => {

          return Object.values(taskEntities).filter((aTask: TaskInterface) => {
            const isCompletedByUser: boolean = aTask.completedBy && aTask.completedBy.id === userId;
            const subjectIsUserId: boolean = aTask.subject && aTask.subject.id === userId;

            return aTask.isComplete && (isCompletedByUser || subjectIsUserId);
          });
        }),
      );
  }

  public findAllTasksCompletedForUserId(userId: number,
                                        reloadTasks: boolean = false): Observable<TaskInterface[]> {

    return this._store.select(({ task }: AppStateInterface) => task.tasks)
    .pipe(
        map((taskEntities: { [id: number ]: TaskInterface }) => {

          return Object.values(taskEntities).filter((aTask: TaskInterface) => {
            const isCompletedByUser: boolean = aTask.completedBy && aTask.completedBy.id === userId;
            const subjectIsUserId: boolean = aTask.subject && aTask.subject.id === userId;

            return aTask.isComplete && !isCompletedByUser && subjectIsUserId;
          });
        }),
      );
  }

  public findAllTasksCompletedByUserId(userId: number, reloadTasks: boolean = false): Observable<TaskInterface[]> {

    return this._store.select(({ task }: AppStateInterface) => task.tasks)
      .pipe(
        map((taskEntities: { [id: number ]: TaskInterface }) => {

          return Object.values(taskEntities).filter((aTask: TaskInterface) => {
            return aTask.isComplete && aTask.completedBy && aTask.completedBy.id === userId;
          });
        }),
      );
  }

  public findOne(id: number): Observable<TaskInterface> {
    return this._store.select(({ task }: AppStateInterface) => task.tasks[id]);
  }

  public findOneByRelatedEntityId(relatedEntityId: number): Observable<TaskInterface> {
    return this._store.select(({ task }: AppStateInterface) => task.tasks)
      .pipe(
        map((taskEntities: { [id: number ]: TaskInterface }) => {

          return Object.values(taskEntities).find((aTask: TaskInterface) => {
            return aTask.relatedEntity && aTask.relatedEntity.entityId === relatedEntityId;
          });
        }),
      );
  }

  public getSelected(): Observable<number> {
    return this._store.select(({ task }: AppStateInterface) => task.selectedTask);
  }

  public getTaskTemplates(): Observable<TaskTemplateInterface[]> {
    this._store.dispatch(new LoadTaskTemplates());

    return this._store.select(({ task }: AppStateInterface) => task.taskTemplates);
  }

  public loadAll(filters?: TaskServiceFilters): Observable<{ [id: number ]: TaskInterface }> {
    this._store.dispatch(new LoadTasks(filters));

    return this._store.select(({ task }: AppStateInterface) => task.tasks);
  }

  public create(task: TaskInterface): Observable<TaskInterface> {
    this._store.dispatch(new CreateTask(task));

    return this._store.select(({ task }: AppStateInterface) => task.currentTask);
  }

  public createSurveyTask(task: TaskInterface): Observable<TaskInterface> {
    this._store.dispatch(new CreateSurveyTask(task));

    return this._store.select(({ task }: AppStateInterface) => task.currentTask);
  }

  public update(taskToUpdate: TaskInterface): Observable<TaskInterface> {
    this._store.dispatch(new UpdateTask(taskToUpdate));

    return this._store.select(({ task }: AppStateInterface) => task.tasks[taskToUpdate.id]);
  }

  public close(task: TaskInterface): void {
    this._store.dispatch(new CloseTask(task));
  }
}
